"use client";

import { useEffect, useRef, useState } from "react";
import { MapPin, Navigation, Phone, Mail, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MapDirections() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const [mapActive, setMapActive] = useState(false);
  const [markerBounce, setMarkerBounce] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const bounceOffsetRef = useRef(0);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    const markerX = canvas.width / (window.devicePixelRatio || 1) / 2;
    const markerY = canvas.height / (window.devicePixelRatio || 1) / 2;
    const drawMap = () => {
      const width = canvas.width / (window.devicePixelRatio || 1);
      const height = canvas.height / (window.devicePixelRatio || 1);
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, "hsl(188, 70%, 80%)");
      gradient.addColorStop(0.5, "hsl(188, 70%, 54%)");
      gradient.addColorStop(1, "hsl(188, 70%, 65%)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = "hsl(40, 30%, 90%)";
      ctx.beginPath();
      ctx.ellipse(width * 0.3, height * 0.4, 80, 60, Math.PI / 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(width * 0.7, height * 0.6, 100, 70, -Math.PI / 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(width * 0.5, height * 0.75, 60, 50, 0, 0, Math.PI * 2);
      ctx.fill();
      const bounce = Math.sin(bounceOffsetRef.current) * 8;
      const finalMarkerY = markerY - bounce;
      ctx.fillStyle = "rgba(0, 0, 0, 0.2)";
      ctx.beginPath();
      ctx.ellipse(markerX, markerY + 35, 8, 4, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "hsl(30, 40%, 40%)";
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(markerX, finalMarkerY);
      ctx.lineTo(markerX, finalMarkerY + 30);
      ctx.stroke();
      ctx.fillStyle = "hsl(134, 47%, 33%)";
      for (let i = 0; i < 5; i++) {
        const angle = Math.PI * 2 * i / 5 - Math.PI / 2;
        ctx.beginPath();
        ctx.ellipse(markerX + Math.cos(angle) * 10, finalMarkerY - 5 + Math.sin(angle) * 10, 15, 6, angle, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = "hsl(14, 100%, 69%)";
      ctx.beginPath();
      ctx.arc(markerX, finalMarkerY, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.beginPath();
      ctx.arc(markerX, finalMarkerY, 6, 0, Math.PI * 2);
      ctx.fill();
    };
    const animate = () => {
      if (markerBounce) {
        bounceOffsetRef.current += 0.1;
      }
      drawMap();
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [markerBounce]);
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => setMarkerBounce(true), 500);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);
  return <section id="map-directions" ref={sectionRef} className="relative w-full h-screen overflow-hidden bg-background">
      {}
      <div className={`relative w-full h-full transition-opacity duration-[var(--duration-slow)] ${isVisible ? "opacity-100" : "opacity-0"}`} onClick={() => setMapActive(true)}>
        <canvas ref={canvasRef} className={`w-full h-full ${!mapActive ? "cursor-pointer" : ""}`} />
        {!mapActive && <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-medium text-foreground text-sm font-medium">
              Click to activate map
            </div>
          </div>}
      </div>

      {}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 w-full max-w-md mx-auto px-4 transition-all duration-[var(--duration-slow)] ease-[var(--ease-out-back)] ${mapActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"}`}>
        <div className="glass-effect p-6 rounded-2xl shadow-strong">
          <div className="flex items-start gap-4 mb-4">
            <div className="shrink-0 bg-primary/20 p-3 rounded-full">
              <MapPin className="size-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-display font-semibold text-foreground mb-1">
                Visit Us
              </h3>
              <p className="text-sm text-muted-foreground">
                Playa del Carmen, Riviera Maya, Mexico
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-sm">
              <Phone className="size-4 text-primary shrink-0" />
              <Link to="tel:+529841234567" className="text-foreground hover:text-primary transition-colors duration-[var(--duration-fast)]">
                +52 984 123 4567
              </Link>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Mail className="size-4 text-primary shrink-0" />
              <Link to="mailto:contact@ecoluxmaya.com" className="text-foreground hover:text-primary transition-colors duration-[var(--duration-fast)]">
                contact@ecoluxmaya.com
              </Link>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Clock className="size-4 text-primary shrink-0" />
              <span className="text-foreground">Monday to Sunday: 8:00 AM – 8:00 PM</span>
            </div>
          </div>

          <Button className="w-full btn-primary gap-2" onClick={() => window.open("https://maps.google.com/?q=Playa+del+Carmen,+Riviera+Maya,+Mexico", "_blank")}>
            <Navigation className="size-4" />
            Get Directions
          </Button>
        </div>
      </div>
    </section>;
}